import { module as BaseModule } from 'modujs'

/**
 * AJAX submit for single-product `form.cart` (simple + variable).
 * Missing variation → in-template warning (no window.alert).
 */
export default class extends BaseModule {
  init () {
    this.onSubmit = this.onSubmit.bind(this)
    this.onButtonClick = this.onButtonClick.bind(this)
    this.onChange = this.onChange.bind(this)
    this.form = this.el.querySelector('form.cart')
    if (!this.form) return

    this.warning = this.ensureWarning()
    this.button = this.form.querySelector('.single_add_to_cart_button')

    // Capture phase: beat WC variation click handler (window.alert) and submit.
    this.form.addEventListener('submit', this.onSubmit, true)
    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick, true)
    }
    this.form.addEventListener('change', this.onChange)
    if (window.jQuery) {
      window.jQuery(this.form).on('show_variation hide_variation reset_data', this.onChange)
    }
  }

  destroy () {
    if (!this.form) return
    this.form.removeEventListener('submit', this.onSubmit, true)
    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClick, true)
    }
    this.form.removeEventListener('change', this.onChange)
    if (window.jQuery) {
      window.jQuery(this.form).off('show_variation hide_variation reset_data', this.onChange)
    }
  }

  ensureWarning () {
    let el = this.el.querySelector('.shop-variation-warning')
    if (el) return el

    el = document.createElement('p')
    el.className = 'shop-variation-warning'
    el.setAttribute('role', 'status')
    el.setAttribute('aria-live', 'polite')
    el.setAttribute('data-visible', 'false')
    el.textContent = (window.wc_add_to_cart_variation_params && window.wc_add_to_cart_variation_params.i18n_make_a_selection_text)
      ? window.wc_add_to_cart_variation_params.i18n_make_a_selection_text
      : 'Please select product options before adding to cart.'

    const button = this.form.querySelector('.single_add_to_cart_button')
    if (button) {
      button.insertAdjacentElement('beforebegin', el)
    } else {
      this.form.appendChild(el)
    }
    return el
  }

  needsVariation () {
    const variationId = this.form.querySelector('input[name="variation_id"]')
    return !!(variationId && (!variationId.value || variationId.value === '0'))
  }

  showWarning () {
    if (!this.warning) return
    this.warning.setAttribute('data-visible', 'false')
    void this.warning.offsetWidth
    this.warning.setAttribute('data-visible', 'true')
    this.warning.classList.add('is-visible')

    const firstSelect = this.form.querySelector('.variations select:not([disabled])')
    if (firstSelect) {
      firstSelect.setAttribute('aria-invalid', 'true')
      firstSelect.focus({ preventScroll: false })
    }
  }

  hideWarning () {
    if (!this.warning) return
    this.warning.setAttribute('data-visible', 'false')
    this.warning.classList.remove('is-visible')
    this.form.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
      el.removeAttribute('aria-invalid')
    })
  }

  onChange () {
    if (!this.needsVariation()) {
      this.hideWarning()
    }
  }

  /**
   * WC marks the button .disabled + .wc-variation-selection-needed and alerts on click.
   */
  onButtonClick (event) {
    if (!this.needsVariation() && !this.button?.classList.contains('disabled')) {
      return
    }

    if (this.needsVariation() || this.button?.classList.contains('wc-variation-selection-needed')) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.showWarning()
    }
  }

  async onSubmit (event) {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    if (this.needsVariation()) {
      event.preventDefault()
      event.stopImmediatePropagation()
      this.showWarning()
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    this.hideWarning()

    const button = form.querySelector('[type="submit"]')
    if (button) {
      button.disabled = true
      button.classList.add('is-loading')
    }

    if (window.jQuery) {
      window.jQuery(document.body).trigger('adding_to_cart', [window.jQuery(button), null])
    }

    const formData = new FormData(form)
    const submitter = event.submitter
    if (submitter && submitter.name) {
      formData.set(submitter.name, submitter.value || '1')
    }
    if (!formData.has('add-to-cart')) {
      const productId = formData.get('product_id') || form.dataset.product_id
      if (productId) formData.set('add-to-cart', String(productId))
    }

    try {
      const response = await fetch(form.action || window.location.href, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html,application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Add to cart failed')

      const fragUrl = (window.wc_cart_fragments_params && window.wc_cart_fragments_params.wc_ajax_url)
        ? window.wc_cart_fragments_params.wc_ajax_url.toString().replace('%%endpoint%%', 'get_refreshed_fragments')
        : '/?wc-ajax=get_refreshed_fragments'

      const fragRes = await fetch(fragUrl, { method: 'POST', credentials: 'same-origin' })
      const fragData = await fragRes.json()

      if (fragData && fragData.fragments && window.jQuery) {
        window.jQuery.each(fragData.fragments, (selector, html) => {
          window.jQuery(selector).replaceWith(html)
        })
        window.jQuery(document.body).trigger('added_to_cart', [
          fragData.fragments,
          fragData.cart_hash,
          window.jQuery(button)
        ])
        window.jQuery(document.body).trigger('wc_fragments_refreshed')
      } else if (window.jQuery) {
        window.jQuery(document.body).trigger('added_to_cart')
      }
    } catch (err) {
      console.error(err)
      form.submit()
    } finally {
      if (button) {
        button.disabled = false
        button.classList.remove('is-loading')
      }
    }
  }
}
